import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Pencil, Eye } from "lucide-react";
import { getWorkflow, getWorkflowRuns } from "../../services/workflowService";
import { canEditWorkflows, getUserRole } from "../../services/authService";
import { useEndpoints } from "../../hooks/useEndpoints";
import { LoadingState, ErrorState } from "../ui/LoadingStates";
import RunVisualizer from "./RunVisualizer";
import GraphView from "./GraphView";

/**
 * Read-only workflow view: header + drag-pannable (read-only) canvas + inputs +
 * result. Run from here; editors jump to the Studio (/workflows/:id/edit).
 */
export default function WorkflowDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [workflow, setWorkflow] = useState(null);
  const [latestRun, setLatestRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [runOpen, setRunOpen] = useState(false);

  const editable = canEditWorkflows();
  const { endpoints } = useEndpoints();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getWorkflow(id);
    if (res.success && res.data) setWorkflow(res.data);
    else setError(res.message || "Failed to load workflow");
    const runsRes = await getWorkflowRuns(id);
    if (runsRes.success && Array.isArray(runsRes.data) && runsRes.data.length > 0) {
      setLatestRun(runsRes.data[0]);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex-1 overflow-auto bg-[#FCFCFC]">
        <LoadingState message="Loading workflow…" size="large" />
      </div>
    );
  }
  if (error || !workflow) {
    return (
      <div className="flex-1 overflow-auto bg-[#FCFCFC] p-8">
        <button
          onClick={() => navigate("/workflows")}
          className="flex items-center gap-1 text-sm text-gray-600 mb-4 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back to workflows
        </button>
        <ErrorState message={error || "Workflow not found"} />
      </div>
    );
  }

  const defn = workflow.latest_definition || null;
  const gated = workflow.is_accessible === false;
  const inputs = Object.entries(defn?.inputs || {});
  const outputs = Object.entries(defn?.outputs || {});
  const selStep = selectedStep ? defn?.steps?.[selectedStep] : null;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <button
          onClick={() => navigate("/workflows")}
          className="flex items-center gap-1 text-sm text-gray-500 mb-2 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Workflows
        </button>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              {workflow.name}
              {workflow.visibility === "provider" && workflow.provider_name && (
                <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                  {workflow.provider_name}
                </span>
              )}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Version v{workflow.latest_version}
              {!editable && (
                <span className="ml-2 inline-flex items-center gap-1 text-amber-700">
                  <Eye className="w-3.5 h-3.5" /> View only ({getUserRole() || "viewer"})
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!gated && (
              <button
                onClick={() => setRunOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                <Play className="w-4 h-4 fill-current" /> Run
              </button>
            )}
            {editable && (
              <button
                onClick={() => navigate(`/workflows/${id}/edit`)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <Pencil className="w-4 h-4" /> Edit in Studio
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto bg-[#FCFCFC]">
        <div className="max-w-[1100px] mx-auto p-8 space-y-6">
          {workflow.description && (
            <Section title="Description">
              <p className="text-sm text-gray-600">{workflow.description}</p>
            </Section>
          )}

          <Section title={`Inputs (${inputs.length})`}>
            {inputs.length === 0 ? (
              <p className="text-sm text-gray-500">No declared inputs.</p>
            ) : (
              <div className="space-y-1.5">
                {inputs.map(([name, spec]) => (
                  <div key={name} className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-gray-800">{name}</span>
                    {spec.required && <span className="text-red-500">*</span>}
                    <span className="text-xs uppercase tracking-wide text-gray-400">{spec.type}</span>
                    {spec.description && <span className="text-gray-400">— {spec.description}</span>}
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Flow">
            <div className="rounded-xl border border-gray-200 bg-white" style={{ height: 460 }}>
              {defn && Object.keys(defn.steps || {}).length > 0 ? (
                <GraphView
                  definition={defn}
                  endpoints={endpoints}
                  run={latestRun}
                  workflowId={id}
                  selectedStep={selectedStep}
                  onSelectStep={setSelectedStep}
                  interactive={false}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-400">No steps.</div>
              )}
            </div>
            {selStep && (
              <div className="mt-3 rounded-lg border border-gray-200 p-3">
                <div className="font-mono text-xs font-semibold text-gray-800 mb-1">{selectedStep}</div>
                {selStep.type === "endpoint_call" ? (
                  Object.entries(selStep.request_payload || {}).map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[160px_1fr] gap-2 text-xs">
                      <span className="font-mono text-gray-500 truncate">{k}</span>
                      <span className="font-mono text-gray-700 truncate">{String(v)}</span>
                    </div>
                  ))
                ) : (
                  <div className="space-y-0.5">
                    {(selStep.branches || []).map((b, i) => (
                      <div key={i} className="font-mono text-xs text-gray-600">{b.when} → {b.next}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Section>

          <Section title="Result object">
            {outputs.length === 0 ? (
              <p className="text-sm text-gray-500">No result mapping declared.</p>
            ) : (
              <div className="space-y-1.5">
                {outputs.map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[180px_1fr] gap-2 items-start">
                    <span className="font-mono text-xs font-semibold text-gray-800">{k}</span>
                    <span className="font-mono text-xs text-gray-500 break-all">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>

      {runOpen && (
        <RunVisualizer workflow={workflow} onClose={() => setRunOpen(false)} />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">{title}</h2>
      {children}
    </div>
  );
}
